import { ApplyDisplayFilterFields, CheckSelection, FallbackMaxAgeSetting, selectChecksPerLayer, selectLatestCheckPerLayer, toCheckLocations } from "../../../../actions/Checks/checkSelection";
import { SUB_LAYER_ROWS } from "../../../../actions/Layers/constants";

export interface SubLayerRow {
    key: string;
    layer: any;
    check: any;
}

interface SubLayerRowsConfig {
    rows?: SUB_LAYER_ROWS;
    filter_location?: string | string[];
    fallback_check_types?: string[];
    fallback_check_max_age_days?: FallbackMaxAgeSetting;
    only_checks_for_this_layer?: boolean;
}

/**
 * Build the rows of the children table (show_children, and each tab of show_tabs).
 * Which checks of a child count is decided by the shared check selection (checkSelection.ts):
 * the checks at filter_location (done for `layer` with only_checks_for_this_layer), otherwise the
 * fallback_check_types within fallback_check_max_age_days (per child) at the layer's facility.
 *
 * - rows = children (default): one row per child, with its latest selected check.
 * - rows = checks: one row per selected check of a child, newest first.
 *   A check keeps its own meta, so the row does not change when the child's meta changes later.
 */
export const getSubLayerRows = (
    children: any[],
    allChecks: any[],
    { rows, filter_location, fallback_check_types, fallback_check_max_age_days, only_checks_for_this_layer }: SubLayerRowsConfig,
    layer?: any,
    facilityOf?: CheckSelection["facilityOf"],
    applyDisplayFilterFields?: ApplyDisplayFilterFields,
): SubLayerRow[] => {
    const selection: CheckSelection = {
        preferred: toCheckLocations(filter_location),
        fallback: fallback_check_types,
        fallbackMaxAgeDays: fallback_check_max_age_days,
        onlyForLayer: only_checks_for_this_layer,
        layer,
        children,
        facilityOf,
        applyDisplayFilterFields,
    };

    if (rows === SUB_LAYER_ROWS.CHECKS) {
        const checksPerChild = selectChecksPerLayer(allChecks, selection);
        return children
            .flatMap((child) => (checksPerChild.get(child.id) ?? []).map((check) => ({ key: `check-${check.check_id}`, layer: child, check })))
            .sort((a, b) => b.check.check_id - a.check.check_id);
    }

    const latestPerChild = selectLatestCheckPerLayer(allChecks, selection);
    return children.map((child) => ({ key: child.id, layer: child, check: latestPerChild.get(child.id) }));
};
